import React, { useRef, useEffect, useState, useCallback } from 'react';
import { Engine } from '../engine/Engine';
import { initNexusMayhem } from '../games/NexusMayhem';
import { submitScore, awardXp, calculateSessionXp } from '../services/GameServices';
import HUD from './HUD';
import UpgradeSelect from './UpgradeSelect';
import GameOver from './GameOver';

/**
 * GameWrapper — React shell around the Canvas engine
 *
 * This is the bridge between React and the engine.
 * Canvas runs independently at 60fps. React only updates
 * the HUD overlay at ~10fps via the uiBridge callback.
 */

export default function GameWrapper({ onBack }) {
  const canvasRef = useRef(null);
  const engineRef = useRef(null);

  // UI state (updated by engine's uiBridge, throttled to ~10fps)
  const [uiState, setUiState] = useState({
    score: 0, kills: 0, combo: 0,
    level: 1, xp: 0, xpToNext: 100,
    hp: 100, maxHp: 100, wave: 1,
    gameOver: false, levelUpPending: false,
    elapsed: 0,
  });

  // Upgrade selection state
  const [upgradeChoices, setUpgradeChoices] = useState(null);
  const [gameOverStats, setGameOverStats] = useState(null);

  // Initialize engine
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const engine = new Engine(canvas);
    engineRef.current = engine;

    // UI bridge
    engine.setUIBridge((data) => {
      setUiState(prev => {
        // Only update if something changed (avoid unnecessary renders)
        for (const key in data) {
          if (prev[key] !== data[key]) return { ...prev, ...data };
        }
        return prev;
      });
    });

    // Level up handler
    engine.onLevelUp = (level) => {
      engine.pause();
      const choices = engine.content.randomUpgrades(3, engine.upgrades);
      setUpgradeChoices(choices);
    };

    // Game over handler
    engine.onGameOver = (stats) => {
      setGameOverStats(stats);
      // Save high score locally
      try {
        const existing = JSON.parse(localStorage.getItem('nm_highscore') || '0');
        if (stats.score > existing) {
          localStorage.setItem('nm_highscore', JSON.stringify(stats.score));
        }
      } catch (e) {}

      // Submit to Supabase (fire and forget)
      const playerName = localStorage.getItem('misk_player_name') || 'Anonymous';
      const meta = { wave: stats.wave, kills: stats.kills, level: stats.level, timeSurvived: stats.timeSurvived };
      submitScore('nexus-mayhem', playerName, stats.score, meta);
      const xp = calculateSessionXp('nexus-mayhem', stats);
      const playerId = localStorage.getItem('misk_player_id') || 'anon_' + Date.now();
      if (!localStorage.getItem('misk_player_id')) localStorage.setItem('misk_player_id', playerId);
      awardXp(playerId, xp, 'nexus-mayhem', { score: stats.score, ...meta });
    };

    // Init game
    initNexusMayhem(engine);
    engine.start();

    return () => {
      engine.stop();
    };
  }, []); // runs ONCE

  // Handle upgrade selection
  const handleUpgradeSelect = useCallback((upgrade) => {
    const engine = engineRef.current;
    if (!engine) return;

    engine.applyUpgrade(upgrade);
    engine.state.levelUpPending = false;
    engine.audio.synthSfx('levelup');
    engine.juice.flash('#a855f7', 0.15);
    engine.juice.emit(engine.width / 2, engine.height / 2, 30, {
      color: '#a855f7',
      speed: 250,
      size: 4,
      life: 0.6,
    });

    setUpgradeChoices(null);
    engine.resume();
  }, []);

  // Handle restart
  const handleRestart = useCallback(() => {
    const engine = engineRef.current;
    if (!engine) return;

    // Reset everything
    engine.entities.clear();
    engine.juice.clearParticles();
    engine.state = {
      score: 0, kills: 0, combo: 0, comboTimer: 0,
      comboDecay: 2.0, level: 1, xp: 0, xpToNext: 100,
      hp: 100, maxHp: 100, wave: 1,
      gameOver: false, levelUpPending: false,
    };
    engine.playerStats = {
      moveSpeed: 200, fireRate: 8, damage: 1,
      projectileCount: 3, projectileSpread: 15, projectileSpeed: 400,
      pickupRadius: 40, critChance: 0, critMultiplier: 1.5,
      shieldHP: 0, chainTargets: 0, chainDamageDecay: 0, healOnKill: 0,
    };
    engine.upgrades = [];
    engine.elapsed = 0;
    engine.systems = [];

    // Re-init
    initNexusMayhem(engine);
    setGameOverStats(null);
    setUpgradeChoices(null);
    engine.resume();
  }, []);

  return (
    <div style={{
      position: 'relative',
      width: '100vw',
      height: '100vh',
      overflow: 'hidden',
      background: '#06060c',
    }}>
      {/* Canvas */}
      <canvas
        ref={canvasRef}
        style={{
          position: 'absolute',
          top: 0, left: 0,
          width: '100%', height: '100%',
          display: 'block',
          cursor: 'none',
        }}
      />

      {/* HUD overlay */}
      <HUD {...uiState} />

      {/* Level up overlay */}
      {upgradeChoices && (
        <UpgradeSelect
          upgrades={upgradeChoices}
          onSelect={handleUpgradeSelect}
        />
      )}

      {/* Game over overlay */}
      {gameOverStats && (
        <GameOver
          stats={gameOverStats}
          onRestart={handleRestart}
        />
      )}
    </div>
  );
}
